import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  githubLicenseService,
  GitHubLicenseSummary,
  GitHubLicenseUser,
} from '../services/githubLicenseService';
import {
  teamViewerLicenseService,
  TeamViewerLicenseSummary,
  TeamViewerLicenseUser,
} from '../services/teamViewerLicenseService';

interface CategoryLicensesProps {
  categoryName: string;
}

export const CategoryLicenses: React.FC<CategoryLicensesProps> = ({ categoryName }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [gitHubSummary, setGitHubSummary] = useState<GitHubLicenseSummary | null>(null);
  const [gitHubUsers, setGitHubUsers] = useState<GitHubLicenseUser[]>([]);
  const [teamViewerSummary, setTeamViewerSummary] = useState<TeamViewerLicenseSummary | null>(null);
  const [teamViewerUsers, setTeamViewerUsers] = useState<TeamViewerLicenseUser[]>([]);

  const isGitHubCategory = categoryName === 'GitHub';
  const isTeamViewerCategory = categoryName === 'TeamViewer';

  useEffect(() => {
    if (!isGitHubCategory && !isTeamViewerCategory) {
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (isGitHubCategory) {
          const [summaryData, userData] = await Promise.all([
            githubLicenseService.getSummary(),
            githubLicenseService.getUsers(),
          ]);

          setGitHubSummary(summaryData);
          setGitHubUsers(userData);
          return;
        }

        const [summaryData, userData] = await Promise.all([
          teamViewerLicenseService.getSummary(),
          teamViewerLicenseService.getUsers(),
        ]);

        setTeamViewerSummary(summaryData);
        setTeamViewerUsers(userData);
      } catch (loadError: any) {
        setError(loadError?.message ?? `Failed to load ${categoryName} licenses and users.`);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [categoryName, isGitHubCategory, isTeamViewerCategory]);

  const gitHubFilteredUsers = useMemo(() => {
    const normalizedSearch = search.toLowerCase().trim();
    if (!normalizedSearch) {
      return gitHubUsers;
    }

    return gitHubUsers.filter((user) =>
      [user.login, user.name, user.email, user.company, user.location]
        .filter(Boolean)
        .some((value) => value!.toLowerCase().includes(normalizedSearch))
    );
  }, [gitHubUsers, search]);

  const teamViewerFilteredUsers = useMemo(() => {
    const normalizedSearch = search.toLowerCase().trim();
    if (!normalizedSearch) {
      return teamViewerUsers;
    }

    return teamViewerUsers.filter((user) =>
      [user.id, user.name, user.email]
        .filter(Boolean)
        .some((value) => value!.toLowerCase().includes(normalizedSearch))
    );
  }, [search, teamViewerUsers]);

  if (!isGitHubCategory && !isTeamViewerCategory) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          {categoryName} Licenses
        </Typography>
        <Paper sx={{ p: 3 }}>
          <Typography variant="body1" color="text.secondary">
            Licenses page placeholder for {categoryName}. Integration will be added in a later
            phase.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {isTeamViewerCategory ? `${categoryName} Users` : `${categoryName} Licenses`}
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Paper sx={{ p: 3 }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {isGitHubCategory ? 'Organization' : 'Account'}
                </Typography>
                <Typography variant="h6">
                  {isGitHubCategory
                    ? (gitHubSummary?.organization ?? '-')
                    : (teamViewerSummary?.accountName ?? 'TeamViewer')}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Licensed Users
                </Typography>
                <Typography variant="h6">
                  {isGitHubCategory
                    ? (gitHubSummary?.licensedUsers ?? 0)
                    : (teamViewerSummary?.licensedUsers ?? 0)}
                </Typography>
              </Paper>
            </Grid>
            {isGitHubCategory && (
              <Grid item xs={12} sm={4}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Available Seats
                  </Typography>
                  <Typography variant="h6">
                    {gitHubSummary?.availableSeats == null
                      ? 'Not configured'
                      : gitHubSummary.availableSeats}
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>

          <Paper sx={{ p: 2, mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Search users"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </Paper>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{isGitHubCategory ? 'User' : 'User ID'}</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  {isGitHubCategory && <TableCell>Company</TableCell>}
                  {isGitHubCategory && <TableCell>Location</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {(isGitHubCategory
                  ? gitHubFilteredUsers.length
                  : teamViewerFilteredUsers.length) === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isGitHubCategory ? 5 : 3} align="center">
                      No {categoryName} users matched the current data set. If you expected results,
                      verify the backend response and provider configuration.
                    </TableCell>
                  </TableRow>
                ) : isGitHubCategory ? (
                  gitHubFilteredUsers.map((user) => (
                    <TableRow key={user.login} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar
                            src={user.avatarUrl}
                            alt={user.login}
                            sx={{ width: 28, height: 28 }}
                          />
                          <a href={user.profileUrl} target="_blank" rel="noreferrer">
                            {user.login}
                          </a>
                        </Box>
                      </TableCell>
                      <TableCell>{user.name || '-'}</TableCell>
                      <TableCell>{user.email || '-'}</TableCell>
                      <TableCell>{user.company || '-'}</TableCell>
                      <TableCell>{user.location || '-'}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  teamViewerFilteredUsers.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>{user.id}</TableCell>
                      <TableCell>{user.name}</TableCell>
                      <TableCell>{user.email || '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};
